import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOutcomeCaptureLook =
  | 'outcome-won' | 'outcome-won-on' | 'outcome-pushed' | 'outcome-pushed-on' | 'outcome-lost' | 'outcome-lost-on'
  | 'outcome-comp' | 'outcome-comp-won' | 'outcome-comp-lost'
  | 'outcome-day' | 'outcome-day-on' | 'outcome-slot' | 'outcome-slot-on'
  | 'outcome-template' | 'outcome-dictate' | 'outcome-dictate-on' | 'outcome-copy' | 'outcome-copy-on' | 'outcome-skip';

export const isWedgeOutcomeCaptureLook = (look: string | undefined): look is WedgeOutcomeCaptureLook => look !== undefined && look.startsWith('outcome-');

/* Released "How'd the call go?" card, shown inline at the end of a brief and in the sheet after a call. Every control here is a box the
   card sizes itself, with the text size, weight and ink the source writes, so none grows to the 44px target and none draws the
   transparent region or anchor (`wedgeSettingsDrawsHit`): the chips wrap 8px apart, 33px tall, and a grown region would reach into the
   row above and below. The three outcome tiles are 1.5px bordered columns with a 14px radius: at rest on the surface fill with the
   separator edge; chosen, on the soft fill and the edge of the outcome color (accent for Won, amber for Pushed, red for Lost). The
   competitor chips take the accent or red pair the same way, the follow-up window chips the amber pair, the time-of-day chips the accent
   tint. The note template chip is 44px tall at least. Talk your outcome (rest and listening), Copy note (rest and copied) and Skip complete the set. Each color reads the exact legacy
   role, so the shipped ink rules keep matching. */
const TILE_RADIUS = 14, PILL = 999, NOTE_RADIUS = 12;

export function wedgeOutcomeCaptureStyle(look: WedgeOutcomeCaptureLook): CSSProperties {
  const r = wedgeExactRole;
  const tile = (tone: 'accent' | 'amber' | 'red', soft: 'accentSoft' | 'amberSoft' | 'redSoft', on: boolean): CSSProperties => ({
    background: on ? r(soft) : r('surface'), border: `1.5px solid ${on ? r(tone) : r('separator')}`, borderRadius: TILE_RADIUS, padding: '16px 8px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: r('font'), transition: `all 200ms ${r('ease')}`,
  });
  const comp = (on: boolean, tone: 'accent' | 'red', soft: 'accentSoft' | 'redSoft'): CSSProperties => ({
    flex: '0 1 auto', background: on ? r(soft) : r('surface'), border: `1.5px solid ${on ? r(tone) : r('separator')}`, borderRadius: PILL, padding: '9px 14px',
    fontSize: 13, fontWeight: 600, color: on ? r(tone) : r('labelSec'), fontFamily: r('font'), cursor: 'pointer', transition: `all 180ms ${r('ease')}`,
  });
  const day = (on: boolean): CSSProperties => ({
    flex: '1 1 auto', minWidth: 72, background: on ? r('amberSoft') : r('surface'), border: `1.5px solid ${on ? r('amber') : r('separator')}`, borderRadius: PILL,
    padding: '9px 10px', fontSize: 13, fontWeight: 600, color: on ? r('amber') : r('labelSec'), fontFamily: r('font'), cursor: 'pointer', transition: `all 180ms ${r('ease')}`,
  });
  const slot = (on: boolean): CSSProperties => ({
    flex: '1 1 auto', background: on ? r('accentTint') : r('surface'), border: `1px solid ${on ? r('accent') : r('separator')}`, borderRadius: PILL,
    padding: '7px 8px', fontSize: 12, fontWeight: 600, color: on ? r('accent') : r('labelTer'), fontFamily: r('font'), cursor: 'pointer', transition: `all 180ms ${r('ease')}`,
  });
  const dictate = (on: boolean): CSSProperties => ({
    marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6, background: on ? r('accentTint') : 'transparent', color: on ? r('accent') : r('labelSec'),
    border: `1px solid ${r('cardBorder')}`, borderRadius: PILL, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, fontFamily: r('font'), cursor: 'pointer',
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
  });
  const copy = (on: boolean): CSSProperties => ({
    marginTop: 12, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px', borderRadius: NOTE_RADIUS,
    background: on ? r('accentSoft') : r('surface'), border: `0.5px solid ${on ? r('accent') : r('separator')}`, color: on ? r('label') : r('labelSec'),
    fontSize: 13, fontWeight: 700, fontFamily: r('font'), cursor: 'pointer', letterSpacing: -0.1,
  });
  switch (look) {
    case 'outcome-won': return tile('accent', 'accentSoft', false);
    case 'outcome-won-on': return tile('accent', 'accentSoft', true);
    case 'outcome-pushed': return tile('amber', 'amberSoft', false);
    case 'outcome-pushed-on': return tile('amber', 'amberSoft', true);
    case 'outcome-lost': return tile('red', 'redSoft', false);
    case 'outcome-lost-on': return tile('red', 'redSoft', true);
    case 'outcome-comp': return comp(false, 'accent', 'accentSoft');
    case 'outcome-comp-won': return comp(true, 'accent', 'accentSoft');
    case 'outcome-comp-lost': return comp(true, 'red', 'redSoft');
    case 'outcome-day': return day(false);
    case 'outcome-day-on': return day(true);
    case 'outcome-slot': return slot(false);
    case 'outcome-slot-on': return slot(true);
    case 'outcome-template': return {
      minHeight: 44, padding: '8px 12px', borderRadius: PILL, background: r('surface'), border: `1px solid ${r('cardBorder')}`, color: r('labelSec'),
      fontSize: 12.5, fontWeight: 700, fontFamily: r('font'), cursor: 'pointer', letterSpacing: -0.1, display: 'inline-flex', alignItems: 'center', gap: 6,
    };
    case 'outcome-dictate': return dictate(false);
    case 'outcome-dictate-on': return dictate(true);
    case 'outcome-copy': return copy(false);
    case 'outcome-copy-on': return copy(true);
    case 'outcome-skip': return {display: 'block', width: '100%', background: 'none', border: 0, color: r('labelSec'), fontSize: 14, fontWeight: 500, padding: 12, marginTop: 4, cursor: 'pointer'};
  }
}
